import { useState, useEffect } from "react";
import { Link } from "wouter";
import {
  useListSquads,
  useCreateSquad,
  useJoinSquad,
  getListSquadsQueryKey,
  getGetCurrentUserQueryKey,
} from "@workspace/api-client-react";
import type { SquadCreationBlockedResponse } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { UpgradePrompt } from "../components/UpgradePrompt";
import { useGetCurrentUser } from "@workspace/api-client-react";

function CreateSquadModal({
  onClose,
  onBlocked,
}: {
  onClose: () => void;
  onBlocked: (blocked: SquadCreationBlockedResponse) => void;
}) {
  const queryClient = useQueryClient();
  const createSquad = useCreateSquad();
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [creatorName, setCreatorName] = useState("");
  const [description, setDescription] = useState("");
  const [goalRace, setGoalRace] = useState("");
  const [weeklyMileageGoal, setWeeklyMileageGoal] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !creatorName.trim()) return;
    setError("");
    createSquad.mutate(
      {
        data: {
          name: name.trim(),
          creatorName: creatorName.trim(),
          description: description.trim() || null,
          goalRace: goalRace.trim() || null,
          weeklyMileageGoal: weeklyMileageGoal ? Number(weeklyMileageGoal) : null,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListSquadsQueryKey() });
          queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() });
          onClose();
        },
        // WI#13 (B3). Before this, the mutation had no onError at all, so a 402 from the
        // billing gate was a silent no-op — the modal just sat there. 402 is not an error the
        // user can fix by retrying, so it swaps the form for the upgrade prompt rather than
        // showing inline text; everything else stays inline and retryable.
        onError: (err) => {
          if (err.status === 402 && err.data) {
            onBlocked(err.data);
            return;
          }
          setError("Couldn't create the squad. Check your connection and try again.");
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="pr-modal"
      >
        <h2 className="font-display text-xl font-bold mb-6">Create Squad</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="pr-label">
              Your Name in This Squad *
            </label>
            <input
              type="text"
              value={creatorName}
              onChange={(e) => setCreatorName(e.target.value)}
              placeholder="Alex Chen"
              autoFocus
              className="pr-field"
            />
          </div>
          <div>
            <label className="pr-label">
              Squad Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="5am Crew"
              className="pr-field"
            />
          </div>
          <div>
            <label className="pr-label">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Who is this squad for?"
              rows={2}
              className="pr-field"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="pr-label">
                Goal Race
              </label>
              <input
                type="text"
                value={goalRace}
                onChange={(e) => setGoalRace(e.target.value)}
                placeholder="Boston 2025"
                className="pr-field"
              />
            </div>
            <div>
              <label className="pr-label">
                Weekly Miles
              </label>
              <input
                type="number"
                value={weeklyMileageGoal}
                onChange={(e) => setWeeklyMileageGoal(e.target.value)}
                placeholder="40"
                min={0}
                className="pr-field"
              />
            </div>
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || !creatorName.trim() || createSquad.isPending}
              className="flex-1 bg-primary text-primary-foreground rounded py-2 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {createSquad.isPending ? "Creating..." : "Create Squad"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function JoinSquadModal({ onClose, initialCode = "" }: { onClose: () => void; initialCode?: string }) {
  const queryClient = useQueryClient();
  const joinSquad = useJoinSquad();
  const [name, setName] = useState("");
  const [inviteCode, setInviteCode] = useState(initialCode);
  const [goalRace, setGoalRace] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !inviteCode.trim()) return;
    setError("");
    joinSquad.mutate(
      {
        data: {
          inviteCode: inviteCode.trim().toUpperCase(),
          name: name.trim(),
          goalRace: goalRace.trim() || null,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListSquadsQueryKey() });
          queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() });
          onClose();
        },
        onError: () => {
          setError("Invalid invite code. Double-check and try again.");
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="pr-modal"
      >
        <h2 className="font-display text-xl font-bold mb-6">Join a Squad</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="pr-label">
              Your Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Alex Chen"
              autoFocus
              className="pr-field"
            />
          </div>
          <div>
            <label className="pr-label">
              Invite Code *
            </label>
            <input
              type="text"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="A1B2C3D4"
              className="pr-field font-mono"
            />
          </div>
          <div>
            <label className="pr-label">
              Goal Race
            </label>
            <input
              type="text"
              value={goalRace}
              onChange={(e) => setGoalRace(e.target.value)}
              placeholder="Boston 2025"
              className="pr-field"
            />
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || !inviteCode.trim() || joinSquad.isPending}
              className="flex-1 bg-primary text-primary-foreground rounded py-2 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {joinSquad.isPending ? "Joining..." : "Join Squad"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default function Squads() {
  const { data: squads, isLoading, isError } = useListSquads();
  // WI#23 — the invite code is the captain's to share; runners don't see it on the list.
  const { data: currentUser } = useGetCurrentUser();
  const managedSquadIds = new Set(
    (currentUser?.members ?? []).filter((m) => m.role === "captain").map((m) => m.squadId),
  );
  const canSeeInvite = (squadId: number) => !!currentUser?.isAdmin || managedSquadIds.has(squadId);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [blocked, setBlocked] = useState<SquadCreationBlockedResponse | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code) {
      setJoinCode(code.toUpperCase());
      setShowJoin(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div className="text-destructive font-mono text-sm font-medium">Unable to load squads</div>
        <div className="text-muted-foreground text-xs text-center max-w-xs">
          There was a problem fetching your data. Check your connection or try refreshing the page.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold mb-1">Squads</h1>
          <p className="text-muted-foreground text-sm">Small groups. Honest check-ins.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowJoin(true)}
            className="border border-border text-foreground px-4 py-2 rounded text-sm font-semibold hover:bg-accent/30 transition-colors"
          >
            Join Squad
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-primary text-primary-foreground px-4 py-2 rounded text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            + New Squad
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="border border-border rounded p-6 animate-pulse">
              <div className="h-5 bg-muted rounded w-1/2 mb-3" />
              <div className="h-3 bg-muted rounded w-3/4 mb-2" />
              <div className="h-3 bg-muted rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : squads?.length === 0 ? (
        <div className="border border-dashed border-border rounded p-16 text-center">
          <p className="font-display text-xl font-bold mb-2">No squads yet.</p>
          <p className="text-muted-foreground text-sm mb-6">
            Create your first squad or join one with an invite code.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => setShowJoin(true)}
              className="border border-border text-foreground px-5 py-2.5 rounded text-sm font-semibold hover:bg-accent/30"
            >
              Join Squad
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="bg-primary text-primary-foreground px-5 py-2.5 rounded text-sm font-semibold"
            >
              Create First Squad
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {squads?.map((squad, i) => (
            <motion.div
              key={squad.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
            >
              <Link href={`/squads/${squad.id}`}>
                <div className="border border-border rounded p-6 hover:border-primary/40 hover:bg-accent/20 transition-all cursor-pointer group">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-display text-lg font-bold transition-colors">
                      {squad.name}
                    </h3>
                    {canSeeInvite(squad.id) && (
                      <span className="font-mono text-xs text-muted-foreground border border-border rounded px-2 py-0.5">
                        {squad.inviteCode}
                      </span>
                    )}
                  </div>
                  {squad.description && (
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{squad.description}</p>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {squad.goalRace && (
                      <span className="text-xs text-muted-foreground font-mono">
                        {squad.goalRace}
                      </span>
                    )}
                    {squad.weeklyMileageGoal && (
                      <span className="text-xs text-muted-foreground font-mono">
                        {squad.weeklyMileageGoal}mi/wk
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}

      {/* The create modal stays MOUNTED behind the upgrade prompt rather than being
          torn down. Unmounting it discarded everything the user had typed — which is
          merely wasteful on the checkout path (they are navigating away to pay), but
          self-contradictory on the degraded path, where the prompt says "please try
          again shortly" about a failure that is ours while deleting the form they
          would retry into. Dismissing the prompt now returns them to their filled-in
          squad. */}
      {showCreate && (
        <CreateSquadModal
          onClose={() => setShowCreate(false)}
          onBlocked={(b) => setBlocked(b)}
        />
      )}
      {showJoin && <JoinSquadModal onClose={() => setShowJoin(false)} initialCode={joinCode} />}
      {blocked && <UpgradePrompt blocked={blocked} onClose={() => setBlocked(null)} />}
    </div>
  );
}
